import NodeToolbar from '@/components/custom/editor/NodeToolbarButton'
import { computePosition, flip, offset, Placement } from '@floating-ui/dom'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { LexicalEditor, NodeKey } from 'lexical'
import { Edit2, LucideIcon, Plus, Trash2 } from 'lucide-react'
import React, { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Position } from '../../type'
import { AIChatMode, useSpAIWriterChatStore } from '../../../stores/sp-ai-writer-chat.store'
import { BaseNode } from '../nodes/BaseNode'
import { getCurrentNode } from '../utils/NodeUtils'
import { KEYS } from '@/components/custom/editor/stringKeys/StringKeys'

type InlineToolbarProps = {
    label: string
    icon: LucideIcon
    onClick: (editor: LexicalEditor) => void
    iconColor?: string
}

const SpNodeToolbarPlugin = () => {
    const ref = useRef<HTMLDivElement>(null)
    const [coords, setCoords] = React.useState<Position | undefined>(undefined)
    const xPosRef = useRef<number>(0)
    const [editor] = useLexicalComposerContext()
    const { openDrawer } = useSpAIWriterChatStore()
    const toolbarActions: InlineToolbarProps[] = [
        {
            label: 'Create Scene with AI',
            icon: Plus,
            onClick: () => openDrawer(AIChatMode.create_scene),
        },
        {
            label: 'Modify Scene with AI',
            icon: Edit2,
            onClick: () =>
                openDrawer(
                    AIChatMode.edit_scene,
                    editor.read(() => {
                        return (getCurrentNode() as BaseNode)?.getCurrentSceneContent()
                    })
                ),
        },
        {
            label: 'Delete',
            icon: Trash2,
            onClick: (editor) => {
                editor.update(() => {
                    const currentNode = getCurrentNode()
                    if (currentNode) {
                        currentNode.remove()
                    }
                })
            },
            iconColor: 'text-red-500',
        },
    ]

    // Calculate xPos only once or on screen resize
    useEffect(() => {
        const updateXPos = async () => {
            const domElem = document.getElementById(KEYS.SP_CONTENT_EDITABLE)
            const floatingElem = ref.current
            if (domElem && floatingElem) {
                const result = await computePosition(domElem, floatingElem, {
                    middleware: [offset(10), flip()],
                    placement: 'right' as Placement,
                })
                xPosRef.current = result.x
            }
        }

        updateXPos()
        window.addEventListener('resize', updateXPos)
        return () => window.removeEventListener('resize', updateXPos)
    }, [])
    const calculatePosition = useCallback(
        (key: NodeKey) => {
            const doPositioning = async () => {
                const _domElem = editor.getElementByKey(key)
                const floatingElem = ref.current
                let coords: Position | undefined = undefined
                if (floatingElem && _domElem) {
                    const currentNode = editor.read(() => getCurrentNode())
                    try {
                        const Ypos = await computePosition(_domElem, floatingElem, {
                            middleware: [offset(10), flip()],
                            placement: 'right-end' as Placement,
                        })
                        coords = { left: xPosRef.current, top: Ypos.y }
                    } catch {
                        console.log('error')
                    }
                    setCoords(coords)
                }
            }
            setTimeout(doPositioning, 0)
        },
        [editor]
    )

    useEffect(() => {
        const unregisterFns = editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const currentNode = getCurrentNode()
                if (currentNode) {
                    calculatePosition(currentNode.__key)
                } else {
                    setCoords(undefined)
                }
            })
        })
        return () => {
            unregisterFns()
        }
    }, [editor])

    const dom = document.getElementById(KEYS.SP_EDITOR_CONTAINER)
    if (!dom) return null
    return createPortal(
        <NodeToolbar coords={coords} toolbarActions={toolbarActions} ref={ref} editor={editor} />,
        dom
    )
}
export default SpNodeToolbarPlugin
